import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { apiErr, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Pencil, Save, Download, Printer, Send, Ban, FileText, PenLine, Settings } from "lucide-react";
import { InvoicePaper, invoiceTotals, printPdfBlob, invStatus } from "@/components/InvoicePaper";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { InvoiceSettingsDialog } from "@/components/InvoiceSettingsDialog";

const SignatureToggle = ({ sig, withSig, setWithSig, isAdmin, openSettings }) => (
  <div className="flex flex-wrap items-center gap-2 border border-border bg-card px-3 py-2 text-xs" data-testid="inv-sig-toggle">
    <PenLine className="h-4 w-4 text-primary" />
    <div className="flex border border-border">
      {[[true, "Print with Signature"], [false, "Print without Signature"]].map(([v, l]) => (
        <button key={l} disabled={!sig} onClick={() => setWithSig(v)} className={`px-3 py-1.5 font-bold uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${withSig === v && sig ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`inv-sig-${v ? "with" : "without"}`}>{l}</button>
      ))}
    </div>
    {!sig && <span className="text-amber-600 dark:text-amber-400" data-testid="inv-sig-missing-note">No signature uploaded yet. {isAdmin ? <button className="underline font-bold" onClick={openSettings} data-testid="inv-sig-open-settings">Upload one in Settings</button> : "Ask an admin to upload one in Settings."}</span>}
  </div>
);

const CancelDialog = ({ open, onClose, onConfirm, busy, serial }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="inv-cancel-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-red-600"><Ban className="h-5 w-5" /> Cancel Invoice {serial}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">The invoice will be stamped CANCELLED. It stays viewable and downloadable, but can no longer be edited or paid.</p>
        <Textarea rows={3} className="rounded-none" placeholder="Reason for cancellation (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="inv-cancel-reason" />
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="inv-cancel-dismiss">Keep Invoice</Button>
          <Button className="rounded-none bg-red-600 hover:bg-red-700 text-white font-bold uppercase" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())} data-testid="inv-cancel-confirm">Cancel Invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const blankBillTo = { name: "", company: "", address: "", city: "", state: "", gst_number: "", phone: "", email: "" };
const blankRow = () => ({ lr_number: "", date: "", vehicle_no: "", from_city: "", to_city: "", description: "TRANSPORTATION OF HOUSEHOLD GOODS", amount: "" });

const fromInvoice = (inv) => ({
  type: inv.invoice_type || "tax", serial: inv.serial, invoiceDate: inv.invoice_date, customerId: inv.customer_id || "",
  billTo: { ...blankBillTo, ...(inv.bill_to || {}) }, preparedBy: inv.prepared_by || inv.created_by || "",
  items: (inv.line_items || []).map((li) => ({ ...blankRow(), ...li, amount: li.amount != null ? String(li.amount) : "" })),
  gstEnabled: inv.gst_enabled !== false, gstMode: inv.gst_mode || "cgst_sgst", gstPercent: inv.gst_percent ?? 18,
  lrs: inv.lrs || [], notes: inv.notes || "",
  meta: { cost_centre: inv.cost_centre || "", po_no: inv.po_no || "", po_date: inv.po_date || "", hsn_code: inv.hsn_code || "", vehicle_no: inv.vehicle_no || "" },
});

const custBillTo = (c) => (c ? { ...blankBillTo, name: c.name || "", company: c.company || "", address: c.address || "", city: c.city || "", state: c.state || "", gst_number: c.gst_number || "", phone: c.phone || "", email: c.email || "" } : null);

// sel: [{lead_id, lr_id, lr_number, order_number}]
const buildFromLrs = async (sel, cust, user) => {
  const pre = await Promise.all(sel.map((s) => api.get(`/leads/${s.lead_id}/invoice-prefill?lr_id=${s.lr_id}`).then((r) => r.data)));
  const first = pre[0] || {};
  const seen = new Set();
  const items = pre.map((p, i) => {
    const firstOfLead = !seen.has(sel[i].lead_id); seen.add(sel[i].lead_id);
    return { ...blankRow(), ...p.invoice_row, amount: String(firstOfLead ? p.invoice_row.amount || 0 : 0) };
  });
  return {
    type: "tax", serial: "", invoiceDate: new Date().toISOString().slice(0, 10), customerId: cust?.id || "",
    billTo: custBillTo(cust) || { ...blankBillTo, ...(first.bill_to || {}), name: first.lr_bill_to || first.bill_to?.name || "", company: first.lr_bill_to || first.bill_to?.company || "" },
    preparedBy: user?.name || "", items: items.length ? items : [blankRow()],
    gstEnabled: true, gstMode: "cgst_sgst", gstPercent: 5, notes: "", lrs: sel,
    meta: { cost_centre: "", po_no: first.po_no || "", po_date: first.po_date || "", hsn_code: first.hsn_code || "", vehicle_no: first.vehicle_no || "" },
  };
};

const toBody = (d) => ({
  invoice_type: d.type, customer_id: d.customerId, bill_to: { ...d.billTo, name: d.billTo.name || d.billTo.company }, invoice_date: d.invoiceDate,
  lrs: d.lrs, notes: d.notes, prepared_by: d.preparedBy,
  line_items: d.items.filter((x) => (x.description || "").trim() || parseFloat(x.amount) > 0).map((x) => ({ ...x, amount: parseFloat(x.amount) || 0 })),
  gst_enabled: d.gstEnabled, gst_mode: d.gstMode, gst_percent: parseFloat(d.gstPercent) || 0, ...d.meta,
});

const DocControls = ({ d, set }) => (
  <div className="flex flex-wrap items-center gap-3 border border-border bg-card px-3 py-2 text-xs" data-testid="inv-page-controls">
    <div className="flex border border-border">
      {[["tax", "Tax Invoice"], ["bog", "Bill of Supply"]].map(([k, l]) => (
        <button key={k} onClick={() => set({ type: k })} className={`px-3 py-1.5 font-bold uppercase transition-colors ${d.type === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`inv-page-type-${k}`}>{l}</button>
      ))}
    </div>
    {d.type === "tax" && (
      <>
        <label className="flex items-center gap-2"><Switch checked={d.gstEnabled} onCheckedChange={(v) => set({ gstEnabled: v })} data-testid="inv-page-gst-toggle" /> Apply GST</label>
        {d.gstEnabled && (
          <>
            <div className="flex border border-border" data-testid="inv-page-gst-mode">
              {[["cgst_sgst", "CGST + SGST"], ["igst", "IGST"]].map(([k, l]) => (
                <button key={k} onClick={() => set({ gstMode: k })} className={`px-3 py-1.5 font-bold uppercase transition-colors ${d.gstMode === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`inv-page-gst-mode-${k}`}>{l}</button>
              ))}
            </div>
            <div className="flex border border-border" data-testid="inv-page-gst-pct">
              {[5, 18].map((p) => (
                <button key={p} onClick={() => set({ gstPercent: p })} className={`px-3 py-1.5 font-bold transition-colors ${Number(d.gstPercent) === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`inv-page-gst-${p}`}>{p}%</button>
              ))}
            </div>
            <span className="text-muted-foreground">{d.gstMode === "igst" ? `IGST ${d.gstPercent}% (full tax)` : `CGST ${Number(d.gstPercent) / 2}% + SGST ${Number(d.gstPercent) / 2}%`}</span>
          </>
        )}
      </>
    )}
  </div>
);

const CustomerPicker = ({ customers, value, onPick }) => (
  <div className="flex flex-wrap items-center gap-2 border border-border bg-card px-3 py-2 text-sm" data-testid="inv-page-customer-picker">
    <span className="text-xs font-bold uppercase text-muted-foreground">Customer</span>
    <select value={value || ""} onChange={(e) => onPick(e.target.value)} className="h-9 min-w-[260px] border border-border bg-background px-2" data-testid="inv-page-customer-select">
      <option value="">Select a customer…</option>
      {customers.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.company} — ${c.name}` : c.name} {c.phone ? `(${c.phone})` : ""}</option>)}
    </select>
  </div>
);

const OrderPicker = ({ avail, sel, onToggle }) => (
  <div className="border border-border bg-card" data-testid="inv-page-order-picker">
    <div className="px-3 py-2 bg-muted text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Orders / LRs on this invoice ({sel.length} selected) · ticking rebuilds the rows</div>
    <div className="max-h-56 overflow-y-auto divide-y divide-border">
      {avail.length === 0 && <div className="p-3 text-xs text-muted-foreground">No LRs found for this customer.</div>}
      {avail.map((a) => (
        <label key={a.lr_id} className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 ${a.matches_customer ? "" : "opacity-70"}`} data-testid={`inv-page-lr-${a.lr_id}`}>
          <input type="checkbox" checked={sel.some((s) => s.lr_id === a.lr_id)} onChange={() => onToggle(a)} />
          <span className="font-mono-num font-bold w-20">{a.lr_number}</span>
          <span className="w-36 truncate">{a.order_number}</span>
          <span className="flex-1 truncate text-muted-foreground">{a.customer_name} · {a.route}</span>
          <span className="font-mono-num">{money(a.order_value)}</span>
        </label>
      ))}
    </div>
  </div>
);

export default function InvoicePage() {
  const { leadId, invoiceId } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cs, setCs] = useState(null);
  const [lead, setLead] = useState(null);
  const [inv, setInv] = useState(null);
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attach, setAttach] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [avail, setAvail] = useState([]);
  const [sig, setSig] = useState("");
  const [withSig, setWithSig] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const loadSig = useCallback(() => { api.get("/settings/signature").then((r) => setSig(r.data.data_url || "")).catch(() => setSig("")); }, []);
  useEffect(() => { loadSig(); }, [loadSig]);
  const invParam = (invoiceId && invoiceId !== "new" ? invoiceId : null) || sp.get("invoice");
  const custParam = sp.get("customer");
  const customerMode = !leadId && !invParam;

  const load = useCallback(async () => {
    try {
      setCs((await api.get("/settings/company")).data);
      if (leadId) setLead((await api.get(`/leads/${leadId}`)).data);
      let existing = null;
      if (invParam) existing = (await api.get(`/invoices/${invParam}`)).data;
      else if (leadId) existing = (await api.get(`/invoices?lead_id=${leadId}`)).data.find((x) => x.status !== "cancelled");
      if (existing) { setInv(existing); setD(fromInvoice(existing)); setEdit(false); return; }
      setInv(null); setEdit(true);
      if (leadId) {
        const l = (await api.get(`/leads/${leadId}`)).data;
        setD(await buildFromLrs((l.lrs || []).map((x) => ({ lead_id: l.id, lr_id: x.id, lr_number: x.lr_number, order_number: l.order_number })), null, user));
        return;
      }
      const custs = (await api.get("/customers")).data;
      setCustomers(custs);
      const cust = custs.find((c) => c.id === custParam) || null;
      const av = cust ? (await api.get(`/lrs/available?customer_id=${cust.id}`)).data.sort((a, b) => Number(b.matches_customer) - Number(a.matches_customer)) : [];
      setAvail(av);
      const sel = av.filter((a) => a.matches_customer).map((a) => ({ lead_id: a.lead_id, lr_id: a.lr_id, lr_number: a.lr_number, order_number: a.order_number }));
      setD(await buildFromLrs(sel, cust, user));
    } catch (e) { toast.error(apiErr(e)); }
  }, [leadId, invParam, custParam]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setD((x) => ({ ...x, ...patch }));
  const on = {
    billTo: (k, v) => setD((x) => ({ ...x, billTo: { ...x.billTo, [k]: v } })),
    item: (i, patch) => setD((x) => ({ ...x, items: x.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) })),
    addRow: () => setD((x) => ({ ...x, items: [...x.items, blankRow()] })),
    addExtra: () => setD((x) => ({ ...x, items: [...x.items, { ...blankRow(), description: "EXTRA CHARGES" }] })),
    removeRow: (i) => setD((x) => ({ ...x, items: x.items.filter((_, idx) => idx !== i) })),
    date: (v) => set({ invoiceDate: v }),
    preparedBy: (v) => set({ preparedBy: v }),
  };

  const toggleLr = async (a) => {
    const has = d.lrs.some((s) => s.lr_id === a.lr_id);
    const sel = has ? d.lrs.filter((s) => s.lr_id !== a.lr_id) : [...d.lrs, { lead_id: a.lead_id, lr_id: a.lr_id, lr_number: a.lr_number, order_number: a.order_number }];
    try {
      const rebuilt = await buildFromLrs(sel, null, user);
      setD((x) => ({ ...x, lrs: sel, items: rebuilt.items, meta: x.meta.po_no ? x.meta : rebuilt.meta }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!(d.billTo.name || d.billTo.company).trim()) { toast.error("Customer name is required"); return null; }
    if (!d.lrs.length) { toast.error("Select at least one order / LR for this invoice"); return null; }
    setBusy(true);
    try {
      const { data } = inv ? await api.put(`/invoices/${inv.id}`, toBody(d)) : await api.post("/invoices", toBody(d));
      toast.success(`Invoice ${data.serial} saved`);
      setInv(data); setD(fromInvoice(data)); setEdit(false);
      if (!invParam) {
        if (leadId) setSp({ invoice: data.id }, { replace: true });
        else navigate(`/dashboard/invoices/${data.id}`, { replace: true });
      }
      return data;
    } catch (e) { toast.error(apiErr(e)); return null; } finally { setBusy(false); }
  };

  const pdfBlob = async (plain = false) => {
    const saved = edit || !inv ? await save() : inv;
    if (!saved) return null;
    const r = await api.get(`/invoices/${saved.id}/pdf?include_carrier_risk=${attach}&plain=${plain}&signature=${!!sig && withSig}`, { responseType: "blob" });
    return { blob: r.data, saved };
  };

  const download = async () => {
    try {
      const res = await pdfBlob(); if (!res) return;
      const url = URL.createObjectURL(res.blob); const a = document.createElement("a");
      a.href = url; a.download = `${res.saved.serial.replace(/\//g, "-")}-invoice.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const print = async () => {
    try { const res = await pdfBlob(); if (res) printPdfBlob(res.blob); } catch (e) { toast.error(apiErr(e)); }
  };

  const printLetterhead = async () => {
    try { const res = await pdfBlob(true); if (res) printPdfBlob(res.blob); } catch (e) { toast.error(apiErr(e)); }
  };

  const cancelInvoice = async (reason) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/void`, { reason });
      setInv(data); setD(fromInvoice(data)); setEdit(false); setCancelOpen(false);
      toast.success(`Invoice ${data.serial} cancelled`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const markSent = async () => {
    try { const { data } = await api.post(`/invoices/${inv.id}/sent`); setInv(data); toast.success("Marked as sent"); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d || !cs) return <div className="p-10 text-center text-muted-foreground" data-testid="invoice-page-loading">Loading invoice…</div>;
  const t = invoiceTotals(d);
  const [stLabel, stCls] = inv ? invStatus(inv) : ["New", "bg-muted text-muted-foreground"];
  const ref = lead?.order_number || [...new Set(d.lrs.map((x) => x.order_number).filter(Boolean))].join(", ");

  return (
    <div className="space-y-4" data-testid="invoice-page">
      <div className="flex flex-wrap items-center gap-2 sticky top-0 z-20 bg-background/90 backdrop-blur-md py-2 -mt-2 border-b border-border">
        {leadId
          ? <Button variant="ghost" className="rounded-none gap-1.5" onClick={() => navigate(`/dashboard/leads?open=${leadId}`)} data-testid="inv-page-back"><ArrowLeft className="h-4 w-4" /> Back to Order</Button>
          : <Button variant="ghost" className="rounded-none gap-1.5" onClick={() => navigate("/dashboard/accounts?tab=invoices")} data-testid="inv-page-back"><ArrowLeft className="h-4 w-4" /> Back to Accounts</Button>}
        <div className="mr-auto min-w-0">
          <div className="font-heading font-bold uppercase tracking-tight truncate flex items-center gap-2">
            {inv ? `Invoice ${inv.serial}` : "New Invoice"}{ref ? ` · ${ref}` : ""}
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${stCls}`} data-testid="inv-page-status">{stLabel}</span>
          </div>
          <div className="text-xs text-muted-foreground">{edit ? "Editing: click any highlighted field" : "View mode"} · Net ₹{t.net.toLocaleString("en-IN")}</div>
        </div>
        <label className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="inv-page-attach" /> Attach risk receipt</label>
        {inv && !inv.sent_at && inv.status !== "cancelled" && <Button variant="ghost" className="rounded-none gap-1.5" onClick={markSent} data-testid="inv-page-mark-sent"><Send className="h-4 w-4" /> Mark Sent</Button>}
        <Button variant="outline" className="rounded-none gap-1.5" onClick={() => setEdit(true)} disabled={edit || inv?.status === "cancelled"} data-testid="inv-page-edit"><Pencil className="h-4 w-4" /> Edit Invoice</Button>
        <Button className="rounded-none gap-1.5" onClick={save} disabled={busy || !edit} data-testid="inv-page-save"><Save className="h-4 w-4" /> Save Changes</Button>
        <Button variant="outline" className="rounded-none gap-1.5" onClick={download} disabled={busy} data-testid="inv-page-download"><Download className="h-4 w-4" /> Download PDF</Button>
        <Button variant="outline" className="rounded-none gap-1.5" onClick={print} disabled={busy} data-testid="inv-page-print"><Printer className="h-4 w-4" /> Print</Button>
        <Button variant="outline" className="rounded-none gap-1.5" onClick={printLetterhead} disabled={busy} title="Prints only the invoice content, without logo, header, address and footer, for your pre-printed letterhead paper" data-testid="inv-page-print-letterhead"><FileText className="h-4 w-4" /> Print on Letterhead</Button>
        {inv && inv.status !== "cancelled" && <Button variant="outline" className="rounded-none gap-1.5 text-red-600 border-red-500/50 hover:bg-red-500/10" onClick={() => setCancelOpen(true)} disabled={busy} data-testid="inv-page-cancel"><Ban className="h-4 w-4" /> Cancel Invoice</Button>}
        {isAdmin && <Button variant="ghost" size="icon" className="rounded-none" onClick={() => setSettingsOpen(true)} title="Settings" data-testid="inv-page-settings"><Settings className="h-4 w-4" /></Button>}
      </div>
      {inv?.status === "cancelled" && <div className="border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400 text-sm p-3" data-testid="inv-cancelled-banner"><b>Cancelled</b>{inv.cancelled_at ? ` on ${new Date(inv.cancelled_at).toLocaleDateString("en-IN")}` : ""}{inv.cancelled_by ? ` by ${inv.cancelled_by}` : ""}{inv.cancel_reason ? ` · Reason: ${inv.cancel_reason}` : ""}</div>}
      <SignatureToggle sig={sig} withSig={withSig} setWithSig={setWithSig} isAdmin={isAdmin} openSettings={() => setSettingsOpen(true)} />
      {customerMode && <CustomerPicker customers={customers} value={custParam} onPick={(id) => setSp(id ? { customer: id } : {}, { replace: true })} />}
      {edit && customerMode && custParam && <OrderPicker avail={avail} sel={d.lrs} onToggle={toggleLr} />}
      {edit && <DocControls d={d} set={set} />}
      {!d.lrs.length && <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm p-3">{customerMode ? "Select a customer and tick the orders to bill." : "This order has no Lorry Receipt yet. Create an LR from the order before saving an invoice."}</div>}
      <div className="overflow-x-auto pb-6"><InvoicePaper d={d} cs={cs} edit={edit} on={on} inv={inv} sigUrl={sig && withSig ? sig : ""} /></div>
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} onConfirm={cancelInvoice} busy={busy} serial={inv?.serial} />
      <InvoiceSettingsDialog open={settingsOpen} onClose={() => { setSettingsOpen(false); loadSig(); }} />
    </div>
  );
}
